import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/ui/Footer';
import { Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen relative z-[1]">
      <Header />

      {/* Content */}
      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-4">Legal</Badge>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-4">
              Privacy <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Policy</span>
            </h1>
            <p className="text-slate-400 text-lg">Last Updated: June 29, 2025</p>
          </div>

          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 md:p-12 space-y-8 text-slate-300 leading-relaxed">
            <div>
              <p>
                This Privacy Policy explains how <strong className="text-white">PHOS Management Inc.</strong> ("we," "us," or "our") collects, uses, discloses, and safeguards your personal information when you use our website or services ("the Service"). By using our Service, you agree to the terms of this Privacy Policy.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">1. Definitions</h2>
              <ul className="space-y-2 list-disc list-inside">
                <li><strong className="text-white">Account:</strong> Your registered account with us.</li>
                <li><strong className="text-white">Affiliate:</strong> Entities under shared ownership or control.</li>
                <li><strong className="text-white">Company:</strong> PHOS Management Inc, 12200 Sibley Road, Riverview MI 48193.</li>
                <li><strong className="text-white">Cookies:</strong> Small files stored on your device to track usage.</li>
                <li><strong className="text-white">Country:</strong> United States (Michigan).</li>
                <li><strong className="text-white">Device:</strong> Any device that accesses the Service (e.g., smartphone, tablet).</li>
                <li><strong className="text-white">Personal Data:</strong> Information that identifies you.</li>
                <li><strong className="text-white">Service:</strong> The PHOS website (phosstudio.net).</li>
                <li><strong className="text-white">Service Provider:</strong> Third parties we work with to operate and analyze the Service.</li>
                <li><strong className="text-white">Third-party Social Media Service:</strong> Platforms like Google, Facebook, or LinkedIn that allow login or account creation.</li>
                <li><strong className="text-white">Usage Data:</strong> Automatically collected data like IP address, browser type, time spent, etc.</li>
                <li><strong className="text-white">You:</strong> The user accessing or using the Service.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">2. What Data We Collect</h2>
              <h3 className="text-xl font-bold text-amber-400 mb-3">Personal Data</h3>
              <p className="mb-3">We may collect:</p>
              <ul className="space-y-1 list-disc list-inside mb-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
              </ul>
              
              <h3 className="text-xl font-bold text-amber-400 mb-3">Usage Data</h3>
              <p className="mb-3">Automatically collected, including:</p>
              <ul className="space-y-1 list-disc list-inside mb-4">
                <li>IP address</li>
                <li>Browser and device details</li>
                <li>Pages visited</li>
                <li>Time and duration of visits</li>
                <li>Mobile device identifiers</li>
              </ul>

              <h3 className="text-xl font-bold text-amber-400 mb-3">Social Media Data</h3>
              <p className="mb-3">If you log in via third-party social media (e.g., Google, Facebook), we may receive:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Name</li>
                <li>Email</li>
                <li>Contact list</li>
                <li>Activity info</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">3. How We Use Your Data</h2>
              <p className="mb-3">We use your information to:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>Provide and maintain the Service</li>
                <li>Manage your Account</li>
                <li>Process transactions</li>
                <li>Contact you with updates or promotions</li>
                <li>Respond to your requests</li>
                <li>Analyze usage and improve features</li>
                <li>Comply with legal obligations</li>
                <li>Conduct business transfers if needed</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">4. Cookies and Tracking Technologies</h2>
              <p className="mb-3">We use:</p>
              <ul className="space-y-2 list-disc list-inside mb-4">
                <li><strong className="text-white">Session Cookies:</strong> Deleted when you close your browser</li>
                <li><strong className="text-white">Persistent Cookies:</strong> Remain after your session ends</li>
                <li><strong className="text-white">Web Beacons / Pixels:</strong> Track engagement and email opens</li>
              </ul>
              <p className="mb-3">Cookie types we use:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li><strong className="text-white">Necessary Cookies:</strong> For core functionality</li>
                <li><strong className="text-white">Acceptance Cookies:</strong> To remember your cookie preferences</li>
                <li><strong className="text-white">Functionality Cookies:</strong> To store preferences like login details</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">5. Sharing Your Information</h2>
              <p className="mb-3">We may share data:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>With Service Providers (for analytics, support, communication)</li>
                <li>With Affiliates (under same privacy standards)</li>
                <li>With Business Partners (to offer relevant services)</li>
                <li>During Business Transfers (mergers, sales, restructuring)</li>
                <li>With other users (if you interact publicly)</li>
                <li>With your consent</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">6. Data Retention</h2>
              <p className="mb-3">We retain:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li><strong className="text-white">Personal Data:</strong> As long as necessary for legal and operational reasons</li>
                <li><strong className="text-white">Usage Data:</strong> Shorter periods unless needed for security or functionality</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">7. Data Transfers</h2>
              <p className="mb-3">Your data may be stored or processed outside your state or country. We ensure:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>Secure handling of data</li>
                <li>Transfers only to countries with appropriate safeguards</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">8. Your Rights</h2>
              <p className="mb-3">You may:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>View, update, or delete your data</li>
                <li>Manage data through your Account</li>
                <li>Contact us to request changes or deletions</li>
              </ul>
              <p className="mt-3"><em>Note: We may retain some data as required by law.</em></p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">9. Security</h2>
              <p>We use industry-standard practices to protect your data. However, no method is 100% secure.</p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">10. Children's Privacy</h2>
              <p>We do not knowingly collect data from children under 13. If we discover such data was collected, we will delete it.</p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">11. External Links</h2>
              <p>Our site may link to third-party websites. We are not responsible for their privacy practices.</p>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">12. Changes to This Policy</h2>
              <p className="mb-3">We may update this Privacy Policy. If we do:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>We'll update the date above</li>
                <li>Notify you by email or through the Service</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-black text-white mb-4">13. Contact Us</h2>
              <p className="mb-4">If you have questions, contact us:</p>
              <div className="space-y-3 bg-slate-800/50 p-6 rounded-lg border border-amber-500/20">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-400" />
                  <a href="mailto:contact@phosstudio.net" className="text-amber-400 hover:text-amber-300 transition-colors">
                    contact@phosstudio.net
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-amber-400" />
                  <a href="tel:+13133626984" className="text-amber-400 hover:text-amber-300 transition-colors">
                    +1 313-362-6984
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/">
              <Button className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div> 
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
